import * as React from "react";

import {
  getDinkusForProduct,
  getHeadingForProduct,
  getImagesForProduct,
  getUrlForProduct,
  PresentationThemeProduct,
  PresentProductColorInfo,
  PriceStatusType,
} from "../model";

import {
  usePlpAnalytics,
  useProductListController_forProduct,
} from "../providers";

import { ProductTile } from "../product-tile";

interface ProductSlideProps {
  product: PresentationThemeProduct;
}

const getFirstKeyedValue = (input: { [key: string]: string }) => {
  let firstKeyedValue;
  for (const [key, value] of Object.entries(input)) {
    if (!firstKeyedValue) {
      firstKeyedValue = value;
      break;
    }
  }
  return firstKeyedValue;
};

const calculateSlidesToShow = (
  colors: PresentProductColorInfo[]
): [number, number, boolean] => {
  // why not use window.innerWidth? well it turns out that value doesn't update correctly and consistently for all resize events - documentElement.clientWidth does.
  const windowWidth = window.document.documentElement.clientWidth;

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  const swatchWidth = isDesktop ? 31 : 38;
  const margin = 8;
  const sizeOfArrow = 24;

  const maxDesktopWidth = 1170;
  let allowedProductWidth = windowWidth;
  if (isMobile) {
    // mobile has padding on the sides of the columns
    allowedProductWidth = allowedProductWidth - (68 + 68 + 11 + 11);
  } else {
    allowedProductWidth =
      allowedProductWidth > maxDesktopWidth
        ? maxDesktopWidth
        : allowedProductWidth;

    if (windowWidth < maxDesktopWidth + 100) {
      allowedProductWidth = allowedProductWidth - 100;
    }
  }

  let numberOfColumns;
  if (isMobile) {
    numberOfColumns = 1;
  } else if (isTablet) {
    numberOfColumns = 3;
  } /* if (isDesktop) */ else {
    numberOfColumns = 4;
  }

  const columnGap = 30;
  allowedProductWidth = allowedProductWidth - (numberOfColumns - 1) * columnGap;
  allowedProductWidth = allowedProductWidth / numberOfColumns;

  // figure out the max swatches we can display in the allowed product width
  let maxSwatchesToDisplay: number;
  let numberOfItems = 0;
  let currentWidth = 0;
  while (1) {
    const candidateWidth = swatchWidth + margin;

    if (currentWidth + candidateWidth < allowedProductWidth) {
      numberOfItems += 1;
      currentWidth += candidateWidth;
    } else {
      break;
    }
  }
  maxSwatchesToDisplay = numberOfItems;
  if (isDesktop && maxSwatchesToDisplay > 5) {
    maxSwatchesToDisplay = 5;
  }

  // figure out how many swatches to display for the colors provided
  let numberOfSwatchesToDisplay = 0;
  currentWidth = 0;
  let partial = false;
  colors.forEach((color) => {
    const candidateWidth = swatchWidth + margin;

    if (numberOfSwatchesToDisplay < maxSwatchesToDisplay) {
      numberOfSwatchesToDisplay += 1;
      currentWidth += candidateWidth;
    } else {
      partial = true;
    }
  });

  // figure out whether we need to show a half swatch at the end of the list on mobile
  let isMinimalWidth = numberOfSwatchesToDisplay < maxSwatchesToDisplay;
  if (!isDesktop && !isMinimalWidth && partial) {
    const maxAllowedWidthWithHalfASwatchShowing =
      allowedProductWidth - swatchWidth / 2 - margin;
    if (currentWidth > maxAllowedWidthWithHalfASwatchShowing) {
      currentWidth = currentWidth - swatchWidth - margin;
      numberOfSwatchesToDisplay = numberOfSwatchesToDisplay - 1;
    }
    numberOfSwatchesToDisplay += 0.5;
    currentWidth += margin + swatchWidth / 2;
  }

  // should we show arrows on the desktop color swatch?
  // if so, then recalculate the number of swatches taking into account there is less width now because of the arrows
  let showArrows = false;
  const isThisADesktopInTermsOfArrows = isDesktop || isTablet;
  if (
    isThisADesktopInTermsOfArrows &&
    colors.length > numberOfSwatchesToDisplay
  ) {
    showArrows = true;
    allowedProductWidth = allowedProductWidth - sizeOfArrow * 2;

    maxSwatchesToDisplay = 0;
    numberOfItems = 0;
    currentWidth = 0;
    while (1) {
      const candidateWidth = swatchWidth + margin;

      if (currentWidth + candidateWidth < allowedProductWidth) {
        numberOfItems += 1;
        currentWidth += candidateWidth;
      } else {
        break;
      }
    }
    maxSwatchesToDisplay = numberOfItems;
    if (isDesktop && maxSwatchesToDisplay > 5) {
      maxSwatchesToDisplay = 5;
    }

    numberOfSwatchesToDisplay = 0;
    currentWidth = sizeOfArrow * 2;
    colors.forEach((color) => {
      const candidateWidth = swatchWidth + margin;

      if (numberOfSwatchesToDisplay < maxSwatchesToDisplay) {
        numberOfSwatchesToDisplay += 1;
        currentWidth += candidateWidth;
      }
    });
  }

  return [numberOfSwatchesToDisplay, currentWidth, showArrows];
};

export const ProductSlide: React.FunctionComponent<ProductSlideProps> = (
  props
) => {
  const { analytics } = usePlpAnalytics();

  const {
    presentationColors: colors,
    presentationAutomaticallySelectedColorKey: automaticallySelectedColorKey,
  } = props.product || { presentationColors: [] };

  const { handleActionThemedProduct } = useProductListController_forProduct();

  const [windowWidth, setWindowWidth] = React.useState(
    window.document.documentElement.clientWidth
  );

  const [slidesToShow, slidesContainerWidth, showArrowsOnColors] =
    React.useMemo(() => calculateSlidesToShow(colors), [colors, windowWidth]);
  const [selectedColor, setSelectedColor] = React.useState(
    automaticallySelectedColorKey
  );

  const images = React.useMemo(
    () => getImagesForProduct(props.product, selectedColor,  !!(automaticallySelectedColorKey && selectedColor && selectedColor === automaticallySelectedColorKey)),
    [props.product, selectedColor]
  );

  const url = React.useMemo(
    () => getUrlForProduct(props.product, selectedColor),
    [props.product, selectedColor]
  );

  const headingText = React.useMemo(() => {
    const headingText = getHeadingForProduct(props.product, selectedColor);
    return headingText;
  }, [props.product]);

  const dinkus = React.useMemo(
    () => getDinkusForProduct(props.product, selectedColor),
    [props.product, selectedColor]
  );

  const priceLabel = React.useMemo(() => {
    const priceLabel = getFirstKeyedValue(props.product.price_label);
    return priceLabel;
  }, [props.product]);

  const priceAfter = React.useMemo(() => {
    const { price_status_au } = props.product || {};

    const priceStatusType = getFirstKeyedValue(
      price_status_au as { [key: string]: string }
    ) as PriceStatusType;

    let priceAfter;
    switch (priceStatusType) {
      case "O":
        break;
      case "SO":
        priceAfter = (
          props.product.price_lowest ?? props.product.price
        ).toFixed(2);
        break;
      case "M":
        priceAfter = (
          props.product.price_lowest ?? props.product.price
        ).toFixed(2);
        break;
      case "SM":
        priceAfter = (
          props.product.price_lowest ?? props.product.price
        ).toFixed(2);
        break;
      default:
        break;
    }

    return priceAfter;
  }, [props.product]);

  const priceBefore = React.useMemo(() => {
    const { price_status_au } = props.product || {};

    const priceStatusType = getFirstKeyedValue(
      price_status_au as { [key: string]: string }
    ) as PriceStatusType;

    let priceBefore = Number(0).toFixed(2);
    switch (priceStatusType) {
      case "O":
        priceBefore = props.product?.rrp?.toFixed(2) || "0.00";
        break;
      case "SO":
        priceBefore = props.product?.rrp?.toFixed(2) || "0.00";
        break;
      case "M":
        priceBefore = props.product?.rrp?.toFixed(2) || "0.00";
        break;
      case "SM":
        priceBefore = props.product?.rrp?.toFixed(2) || "0.00";
        break;
      default:
        priceBefore = props.product?.rrp?.toFixed(2) || "0.00";
        break;
    }

    return priceBefore;
  }, [props.product]);

  React.useEffect(() => {
    const onResize = () => {
      const newWindowWidth = window.document.documentElement.clientWidth;
      setWindowWidth(newWindowWidth);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const handleActionColorSwatch = (color: PresentProductColorInfo) => {
    setSelectedColor(color.key);

    const fredhopper_magento_id_for_the_whole_item =
      props.product.magento_id_product;
    const color_swatch_selected_value = color.value;
    const corresponding_grouped_product_name_for_color_swatch =
      getHeadingForProduct(props.product, color.key);
    analytics.clickProductColorSwatch({
      fredhopper_magento_id_for_the_whole_item,
      color_swatch_selected_value,
      corresponding_grouped_product_name_for_color_swatch,
    });
  };

  const handleAction = () => {
    handleActionThemedProduct(props.product);

    // just let the link take them where they need to go
  };

  return (
    <ProductTile
      url={url}
      handleAction={handleAction}
      images={images}
      heading={headingText}
      priceLabel={priceLabel}
      priceAfter={priceAfter}
      priceBefore={priceBefore}
      colors={colors}
      colorSlidesContainerWidth={slidesContainerWidth}
      colorSlidesToShow={slidesToShow}
      showArrowsOnColors={showArrowsOnColors}
      selectedColor={selectedColor}
      handleActionColorSwatch={handleActionColorSwatch}
      disableImageSlider
      dinkus={dinkus}
    />
  );
};
